import React, { useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import axios from "axios";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import LoadingButton from "@mui/lab/LoadingButton";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import MarkEmailReadRoundedIcon from "@mui/icons-material/MarkEmailReadRounded";
import AuthLayout from "../auth/AuthLayout";
import { Refusal } from "../workspaces/WorkspaceUi";

export default function ForgetPassword() {
  const [email, setEmail] = useState("");
  const [fieldError, setFieldError] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setResult(null);
    if (!email.trim()) {
      setFieldError("Enter the email you use for Business Flows.");
      return;
    }
    setFieldError("");
    setBusy(true);
    try {
      const response = await axios.post("/neoPublic/forgot-password", {
        email: email.trim().toLowerCase(),
      });
      setResult({ success: Boolean(response.data?.success), msg: response.data?.msg });
    } catch (err) {
      setResult({
        success: false,
        msg: err?.response?.data?.msg || "We couldn't send the email right now. Try again in a moment.",
      });
    }
    setBusy(false);
  };

  if (result?.success) {
    return (
      <AuthLayout>
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: 3,
            bgcolor: "primary.main",
            color: "secondary.main",
            display: "grid",
            placeItems: "center",
            mb: 3,
          }}
        >
          <MarkEmailReadRoundedIcon />
        </Box>
        <Typography variant="h4" component="h1" sx={{ mb: 1 }}>
          Check your inbox
        </Typography>
        <Typography variant="subtitle1" sx={{ mb: 4 }}>
          {result.msg || "We've sent password reset instructions to"} <strong>{email.trim()}</strong>
        </Typography>
        <Stack spacing={1.5}>
          <Button component={RouterLink} to="/login" variant="contained" size="large" fullWidth>
            Back to log in
          </Button>
          <Button variant="text" onClick={() => setResult(null)}>
            Use a different email
          </Button>
        </Stack>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <Link
        component={RouterLink}
        to="/login"
        sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, mb: 3, fontWeight: 500 }}
      >
        <ArrowBackRoundedIcon fontSize="small" /> Back to log in
      </Link>
      <Typography variant="h4" component="h1" sx={{ mb: 1 }}>
        Reset your password
      </Typography>
      <Typography variant="subtitle1" sx={{ mb: 4 }}>
        Enter your account email and we'll send you instructions to reset it.
      </Typography>

      {result && !result.success && <Refusal sx={{ mb: 3 }}>{result.msg}</Refusal>}

      <Box component="form" onSubmit={submit} noValidate>
        <Stack spacing={2.5}>
          <TextField
            label="Work email"
            type="email"
            autoComplete="username"
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={Boolean(fieldError)}
            helperText={fieldError}
          />
          <LoadingButton type="submit" variant="contained" size="large" loading={busy} fullWidth>
            Send reset instructions
          </LoadingButton>
        </Stack>
      </Box>

      <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mt: 4 }}>
        Don't have an account?{" "}
        <Link component={RouterLink} to="/signup">
          Start your free trial
        </Link>
      </Typography>
    </AuthLayout>
  );
}
